import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Save, Shield, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
  createProfile,
  updateProfile,
  listPermissions,
  type Profile,
  type Permission,
} from "@/services/profilesService";
import { PermissionTree } from "./PermissionTree";

interface ProfileFormProps {
  onBack: () => void;
  onSave: () => void;
  editingProfile: Profile | null;
}

interface FormData {
  name: string;
  description: string;
  permission_ids: number[];
  is_active: boolean;
}

export const ProfileForm = ({
  onBack,
  onSave,
  editingProfile,
}: ProfileFormProps) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const form = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      permission_ids: [],
      is_active: true,
    },
  });

  useEffect(() => {
    loadFormData();
  }, [editingProfile]);

  const loadFormData = async () => {
    setLoading(true);
    try {
      const permissionsResponse = await listPermissions();

      if (permissionsResponse.success && permissionsResponse.data) {
        setPermissions(permissionsResponse.data);
      } else {
        toast.error(permissionsResponse.error?.message || "Erro ao carregar permissões");
      }

      if (editingProfile) {
        form.setValue("name", editingProfile.name);
        form.setValue("description", editingProfile.description || "");
        form.setValue("permission_ids", editingProfile.permissions.map(p => p.id));
        form.setValue("is_active", editingProfile.is_active);
      }
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!data.name.trim()) {
      toast.error("Nome do perfil é obrigatório");
      return;
    }

    if (!data.description.trim()) {
      toast.error("Descrição é obrigatória");
      return;
    }

    if (data.permission_ids.length === 0) {
      toast.error("Selecione pelo menos uma permissão");
      return;
    }

    setSaving(true);
    try {
      if (editingProfile) {
        const response = await updateProfile(editingProfile.id, {
          name: data.name,
          description: data.description,
          permission_ids: data.permission_ids,
          is_active: data.is_active,
        });

        if (response.success) {
          toast.success("Perfil atualizado com sucesso!");
          onSave();
        } else {
          toast.error(response.error?.message || "Erro ao atualizar perfil");
        }
      } else {
        const response = await createProfile({
          name: data.name,
          description: data.description,
          permission_ids: data.permission_ids,
          is_active: data.is_active,
        });

        if (response.success) {
          toast.success("Perfil criado com sucesso!");
          onSave();
        } else {
          toast.error(response.error?.message || "Erro ao criar perfil");
        }
      }
    } catch (error) {
      toast.error("Erro ao salvar perfil");
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionChange = (permissionIds: number[]) => {
    form.setValue("permission_ids", permissionIds);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Shield}
        title={editingProfile ? "Editar Perfil" : "Novo Perfil"}
        description={
          editingProfile
            ? "Modifique as informações e permissões do perfil"
            : "Defina um novo perfil de acesso com suas respectivas permissões"
        }
        onBack={onBack}
        backDisabled={saving}
        actions={
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={saving}
            className="bg-gradient-primary text-white hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar Perfil
              </>
            )}
          </Button>
        }
      />

      <main className="container mx-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Configure os dados principais do perfil de acesso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome do Perfil *</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Administrador, Analista..."
                    disabled={saving}
                    {...form.register("name")}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={form.watch("is_active").toString()}
                    onValueChange={(value) => form.setValue("is_active", value === "true")}
                    disabled={saving}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Ativo</SelectItem>
                      <SelectItem value="false">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Descrição *</Label>
                <Textarea
                  id="description"
                  placeholder="Descreva as responsabilidades e escopo deste perfil..."
                  rows={4}
                  disabled={saving}
                  {...form.register("description")}
                />
              </div>
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card>
            <CardHeader>
              <CardTitle>Permissões</CardTitle>
              <CardDescription>
                Selecione as permissões que este perfil terá no sistema. Organize por categoria e recurso com granularidade CRUD.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {permissions.length > 0 ? (
                <>
                  <div className="max-h-[600px] overflow-y-auto border rounded-lg p-4">
                    <PermissionTree
                      permissions={permissions}
                      selectedIds={form.watch("permission_ids")}
                      onSelectionChange={handlePermissionChange}
                      readOnly={saving}
                    />
                  </div>
                  {form.watch("permission_ids").length > 0 && (
                    <div className="mt-4 flex items-center space-x-2">
                      <Badge variant="secondary">
                        {form.watch("permission_ids").length} permissão
                        {form.watch("permission_ids").length !== 1 ? "ões" : ""}{" "}
                        selecionada{form.watch("permission_ids").length !== 1 ? "s" : ""}
                      </Badge>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">
                    Nenhuma permissão disponível
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

